import { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Alert, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell, faBellSlash } from '@fortawesome/free-solid-svg-icons'
import {
  useIssueNotificationPreference,
  useCreateIssueNotificationPreference,
  useUpdateIssueNotificationPreference
} from 'api/hooks/use-issue-notification-preference'
import { useMunicipalityIssues } from 'api/hooks/use-municipality-issues'
import {
  useIssueSubscriptions,
  useCreateIssueSubscription,
  useUpdateIssueSubscription,
  useDeleteIssueSubscription
} from 'api/hooks/use-issue-subscriptions'

const NotificationConfigModal = ({ show, onHide }) => {
  const { data: preference, isLoading: prefLoading } = useIssueNotificationPreference()
  const createPreference = useCreateIssueNotificationPreference()
  const updatePreference = useUpdateIssueNotificationPreference()

  const { data: issues, isLoading: issuesLoading } = useMunicipalityIssues({})
  const { data: subscriptions, isLoading: subsLoading } = useIssueSubscriptions()
  const createSubscription = useCreateIssueSubscription()
  const updateSubscription = useUpdateIssueSubscription()
  const deleteSubscription = useDeleteIssueSubscription()

  const [formData, setFormData] = useState({
    enabled: false,
    frequency: 'weekly',
    day_of_week: 0,
    day_of_month: 1
  })

  const [error, setError] = useState(null)

  useEffect(() => {
    if (preference?.id) {
      setFormData({
        enabled: preference.enabled || false,
        frequency: preference.frequency || 'weekly',
        day_of_week: preference.day_of_week ?? 0,
        day_of_month: preference.day_of_month ?? 1
      })
    }
  }, [preference])

  const handleChange = async (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))

    if (field === 'enabled') {
      try {
        if (value === true && !preference?.id) {
          await createPreference.mutateAsync({
            ...formData,
            enabled: true
          })
        } else if (preference?.id) {
          await updatePreference.mutateAsync({
            ...formData,
            enabled: value
          })
        }
      } catch (err) {
        console.error('Error updating notification preference:', err)
        setError(err.message || 'Failed to update notifications. Please try again.')
        setFormData((prev) => ({ ...prev, enabled: !value }))
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    try {
      if (preference?.id) {
        await updatePreference.mutateAsync(formData)
      } else {
        await createPreference.mutateAsync(formData)
      }
      toast.success('Notification preferences saved successfully!')
      onHide()
    } catch (err) {
      console.error('Error saving notification preferences:', err)
      setError(err.message || 'Failed to save preferences. Please try again.')
    }
  }

  const getSubscription = (municipalityIssueId) => {
    return subscriptions?.find(
      (sub) => sub.municipality_issue_id === municipalityIssueId
    )
  }

  const handleToggleSubscription = async (issue) => {
    setError(null)
    const sub = getSubscription(issue.id)
    try {
      if (sub) {
        await deleteSubscription.mutateAsync(sub.id)
      } else {
        await createSubscription.mutateAsync({
          municipality_issue_id: issue.id,
          notify_on_new_log: true,
          notify_on_resolution: true,
          notify_on_snooze_expiration: true
        })
      }
    } catch (err) {
      console.error('Error toggling subscription:', err)
      setError(err.message || 'Failed to update subscription. Please try again.')
    }
  }

  const handleUpdateSubscriptionSetting = async (issue, field, value) => {
    setError(null)
    const sub = getSubscription(issue.id)
    if (!sub) return
    try {
      await updateSubscription.mutateAsync({
        subscriptionId: sub.id,
        params: {
          notify_on_new_log: sub.notify_on_new_log,
          notify_on_resolution: sub.notify_on_resolution,
          notify_on_snooze_expiration: sub.notify_on_snooze_expiration,
          [field]: value
        }
      })
    } catch (err) {
      console.error('Error updating subscription setting:', err)
      setError(err.message || 'Failed to update setting. Please try again.')
    }
  }

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ]

  const daysOfMonth = Array.from({ length: 31 }, (_, i) => i + 1)

  const isLoading = prefLoading || issuesLoading || subsLoading

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3">
          Configure Issue Notifications
          <div className="d-flex gap-2">
            <Button
              variant={!formData.enabled ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => handleChange('enabled', false)}
            >
              <FontAwesomeIcon icon={faBellSlash} className="me-1" />
              Off
            </Button>
            <Button
              variant={formData.enabled ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => handleChange('enabled', true)}
            >
              <FontAwesomeIcon icon={faBell} className="me-1" />
              On
            </Button>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <Form onSubmit={handleSubmit}>
            {formData.enabled && (
              <div className="d-flex align-items-center flex-wrap gap-2 mb-3 px-4 py-3 fs-5" style={{ backgroundColor: '#f3f4f6', borderLeft: '4px solid #6b7280', color: '#1f2937' }}>
                <span>Send me an email</span>
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={formData.frequency}
                  onChange={(e) => handleChange('frequency', e.target.value)}
                >
                  <option value="daily">daily</option>
                  <option value="weekly">weekly</option>
                  <option value="monthly">monthly</option>
                </Form.Select>

                {formData.frequency === 'weekly' && (
                  <>
                    <span>on</span>
                    <Form.Select
                      size="sm"
                      style={{ width: 'auto' }}
                      value={formData.day_of_week}
                      onChange={(e) =>
                        handleChange('day_of_week', parseInt(e.target.value))
                      }
                    >
                      {daysOfWeek.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </Form.Select>
                  </>
                )}

                {formData.frequency === 'monthly' && (
                  <>
                    <span>on day</span>
                    <Form.Select
                      size="sm"
                      style={{ width: 'auto' }}
                      value={formData.day_of_month}
                      onChange={(e) =>
                        handleChange('day_of_month', parseInt(e.target.value))
                      }
                    >
                      {daysOfMonth.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </Form.Select>
                  </>
                )}
              </div>
            )}

            <hr />

            <h6 className="mb-3">Which issues would you like to be notified of?</h6>
            <div style={!formData.enabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>Issue</th>
                  <th className="text-center">Subscribed</th>
                  <th className="text-center">New Logs</th>
                  <th className="text-center">Resolved</th>
                  <th className="text-center">Snooze Expired</th>
                </tr>
              </thead>
              <tbody>
                {issues?.map((issue) => {
                  const sub = getSubscription(issue.id)
                  const isOn = !!sub
                  return (
                    <tr key={issue.id}>
                      <td>{issue?.issue?.name}</td>
                      <td className="text-center align-middle">
                        <Form.Check
                          type="switch"
                          id={`sub-toggle-${issue.id}`}
                          checked={isOn}
                          onChange={() => handleToggleSubscription(issue)}
                        />
                      </td>
                      <td className="text-center align-middle">
                        <Form.Check
                          type="checkbox"
                          id={`new-log-${issue.id}`}
                          checked={isOn && (sub?.notify_on_new_log ?? false)}
                          disabled={!isOn}
                          onChange={(e) =>
                            handleUpdateSubscriptionSetting(issue, 'notify_on_new_log', e.target.checked)
                          }
                        />
                      </td>
                      <td className="text-center align-middle">
                        <Form.Check
                          type="checkbox"
                          id={`resolution-${issue.id}`}
                          checked={isOn && (sub?.notify_on_resolution ?? false)}
                          disabled={!isOn}
                          onChange={(e) =>
                            handleUpdateSubscriptionSetting(issue, 'notify_on_resolution', e.target.checked)
                          }
                        />
                      </td>
                      <td className="text-center align-middle">
                        <Form.Check
                          type="checkbox"
                          id={`snooze-${issue.id}`}
                          checked={isOn && (sub?.notify_on_snooze_expiration ?? false)}
                          disabled={!isOn}
                          onChange={(e) =>
                            handleUpdateSubscriptionSetting(issue, 'notify_on_snooze_expiration', e.target.checked)
                          }
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            </div>

            <Row className="mt-4">
              <Col>
                <Button variant="light" onClick={onHide}>
                  Close
                </Button>
              </Col>
              <Col className="text-end">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={createPreference.isLoading || updatePreference.isLoading}
                >
                  {createPreference.isLoading || updatePreference.isLoading
                    ? 'Saving...'
                    : 'Save Preferences'}
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default NotificationConfigModal
